import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdaptiveScale } from '../../hooks/useAdaptiveScale';
import PageHeader from '../layout/PageHeader';
import { getWarningPage } from '../../api';
import { EarlyWarningInfo, IPage } from '../../api/types';
import styles from '../../styles/components/pages/warning-detail.module.css';

const WarningDetail: React.FC = () => {
  const { scaleFactor, deviceType } = useAdaptiveScale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [warningData, setWarningData] = useState<IPage<EarlyWarningInfo> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedDate, setSelectedDate] = useState<string>('');

  // 获取URL参数
  const deptId = searchParams.get('deptId');
  const identity = searchParams.get('identity');
  const warningCode = searchParams.get('warningCode');
  const deptName = searchParams.get('deptName');

  // 获取预警类型名称
  const getWarningTypeName = (code: string): string => {
    const typeMap: Record<string, string> = {
      'CODE0010001': '食堂建账',
      'CODE0010002': '资金来源',
      'CODE0010003': '食堂结余',
      'CODE0010004': '餐费补助',
      'CODE0010005': '菜价对比',
      'CODE0010006': '余额退还',
      'CODE0010007': '食材≥70%',
      'CODE0010008': '财务公开',
      'CODE0010009': '餐价公开',
      'CODE0010010': '工资发放',
      'CODE0010011': '人员配置',
      'CODE0010012': '经费制度',
      'CODE0010013': '同餐同价',
      'CODE0020001': '带量食谱公示',
      'CODE0020002': '每周2次奶豆制品',
      'CODE0020003': '正餐食材≥6种',
      'CODE0020004': '科普宣传',
      'CODE0030001': '进货查验',
      'CODE0030002': '食品留样',
      'CODE0030003': '食品添加',
      'CODE0030004': '餐具消毒',
      'CODE0030005': '餐碗消毒',
      'CODE0030006': '刀砧消毒',
      'CODE0030007': '肉机消毒',
      'CODE0030008': '病媒防制',
      'CODE0030009': '厨废净化',
      'CODE0030010': '领导陪餐',
      'CODE0030011': '安全投诉',
      'CODE0030012': '信息公开',
      'CODE0030013': '明厨亮灶',
      'CODE0030014': '健康上岗',
      'CODE0030015': '食品检测'
    };
    return typeMap[code] || code;
  };

  // 加载预警数据
  useEffect(() => {
    const loadWarningData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const params = {
          current: currentPage,
          size: pageSize,
          deptId: deptId ? deptId : undefined,
          type: warningCode || undefined,
          earlyWarningDate: selectedDate || undefined
        };
        
        const data = await getWarningPage(params);
        console.log('预警详情数据:', data);
        
        setWarningData(data.data);
      } catch (err) {
        console.error('获取预警详情失败:', err);
        setError(err instanceof Error ? err.message : 'Failed to load warning data');
      } finally {
        setLoading(false);
      }
    };

    loadWarningData();
  }, [currentPage, deptId, warningCode, selectedDate, pageSize]);

  // 返回主页面
  const handleBack = () => {
    const params = new URLSearchParams();
    if (deptId) params.set('deptId', deptId);
    if (identity) params.set('identity', identity);
    if (deptName) params.set('deptName', deptName);
    navigate(`/?${params.toString()}`);
  };

  // 日期筛选处理
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setCurrentPage(1); // 重置到第一页
  };

  const handleDateReset = () => {
    setSelectedDate('');
    setCurrentPage(1); // 重置到第一页
  };

  // 分页处理
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageJump = (page: number) => {
    if (page >= 1 && page <= (warningData?.pages || 1)) {
      setCurrentPage(page);
    }
  };

  // 渲染表格骨架屏
  const renderTableSkeleton = () => {
    return (
      <div className={styles.tableContainer}>
        <table className={styles.warningTable}>
          <thead>
            <tr>
              <th>预警日期</th>
              <th>预警类型</th>
              <th>预警内容</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: pageSize }, (_, index) => (
              <tr key={index} className={styles.skeletonRow}>
                <td className={styles.skeletonCell}>
                  <div className={styles.skeletonLine}></div>
                </td>
                <td className={styles.skeletonCell}>
                  <div className={styles.skeletonLine}></div>
                </td>
                <td className={styles.skeletonCell}>
                  <div className={styles.skeletonLine}></div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // 渲染分页器
  const renderPagination = () => {
    if (!warningData || warningData.pages <= 1) return null;

    const { current, pages, total } = warningData;
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          共 {total} 条记录，第 {current} / {pages} 页
        </div>
        
        <div className={styles.paginationControls}>
          <button
            className={`${styles.pageButton} ${current === 1 || loading ? styles.disabled : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={current === 1 || loading}
          >
            首页
          </button>
          
          <button
            className={`${styles.pageButton} ${current === 1 || loading ? styles.disabled : ''}`}
            onClick={() => handlePageChange(current - 1)}
            disabled={current === 1 || loading}
          >
            上一页
          </button>
          
          {pageNumbers.map(page => (
            <button
              key={page}
              className={`${styles.pageButton} ${current === page ? styles.active : ''} ${loading ? styles.disabled : ''}`}
              onClick={() => handlePageChange(page)}
              disabled={loading}
            >
              {page}
            </button>
          ))}
          
          <button
            className={`${styles.pageButton} ${current === pages || loading ? styles.disabled : ''}`}
            onClick={() => handlePageChange(current + 1)}
            disabled={current === pages || loading}
          >
            下一页
          </button>
          
          <button
            className={`${styles.pageButton} ${current === pages || loading ? styles.disabled : ''}`}
            onClick={() => handlePageChange(pages)}
            disabled={current === pages || loading}
          >
            尾页
          </button>
        </div>
        
        <div className={styles.pageJump}>
          <span>跳转到</span>
          <input
            type="number"
            min="1"
            max={pages}
            className={styles.pageInput}
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !loading) {
                const target = e.target as HTMLInputElement;
                const page = parseInt(target.value);
                if (page && page >= 1 && page <= pages) {
                  handlePageJump(page);
                  target.value = '';
                }
              }
            }}
          />
          <span>页</span>
        </div>
      </div>
    );
  };

  return (
    <div 
      className={`${styles.detailContainer} responsive-container scale-viewport`}
      style={{ 
        '--scale-factor': scaleFactor,
        '--device-type': deviceType 
      } as React.CSSProperties}
    >
      <PageHeader />
      
      <main className={styles.mainContent}>
        <div className={styles.contentCard}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              ← 返回
            </button>
            <h1 className={styles.title}>
              预警详情 - {warningCode ? getWarningTypeName(warningCode) : '全部预警'}
            </h1>
          </div>
          
          <div className={styles.content}>
            {/* 日期筛选器 */}
            <div className={styles.filterSection}>
              <div className={styles.dateFilter}>
                <label htmlFor="dateInput" className={styles.dateLabel}>
                  筛选日期:
                </label>
                <input
                  id="dateInput"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className={styles.dateInput}
                  disabled={loading}
                />
                <button
                  onClick={handleDateReset}
                  className={styles.resetButton}
                  disabled={loading || !selectedDate}
                >
                  重置
                </button>
              </div>
            </div>

            {error && (
              <div className={styles.error}>
                加载失败: {error}
              </div>
            )}
            
            {loading ? (
              renderTableSkeleton()
            ) : warningData && warningData.records.length > 0 ? (
              <>
                <div className={styles.tableContainer}>
                  <table className={styles.warningTable}>
                    <thead>
                      <tr>
                        <th>预警日期</th>
                        <th>预警类型</th>
                        <th>预警内容</th>
                      </tr>
                    </thead>
                    <tbody>
                      {warningData.records.map((record) => (
                        <tr key={record.id}>
                          <td>{record.earlyWarningDate}</td>
                          <td>{getWarningTypeName(record.type)}</td>
                          <td>{record.content}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {renderPagination()}
              </>
            ) : (
              <div className={styles.empty}>
                暂无预警数据
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WarningDetail;
